import type { CalendarEventLabels } from "@dropbox/dash-component-library/";
import {
  getFormattedEventTime as _getFormattedEventTime,
  dateFormatterFn,
  getFullDate,
} from "@mirage/shared/util/calendar";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { calculateEventTimeInfo } from "./utils";

export const useTranslatedLabels = (): Pick<
  CalendarEventLabels,
  | "attendingLabel"
  | "descriptionLabel"
  | "getNumAttendeesLabel"
  | "getFormattedEventTime"
  | "getRelativeTime"
  | "dateFormatterFn"
  | "timeLabel"
  | "relativeTimeLabel"
  | "getFullDate"
  | "showMore"
  | "showLess"
  | "getDeclinedEventLabel"
  | "guestLabel"
  | "hostLabel"
> => {
  const { t } = useTranslation();
  const getNumAttendeesLabel = useCallback(
    (count: number) => t("meeting_attendee", { count }),
    [t],
  );

  const getFormattedEventTime = useCallback(
    (params: { startTime: number; endTime: number; isAllDay?: boolean }) => {
      if (params.isAllDay) {
        return t("all_day");
      }
      return _getFormattedEventTime(params.startTime, params.endTime);
    },
    [t],
  );

  const getDeclinedEventLabel = useCallback(
    (count: number) =>
      count === 1
        ? t("declined_event_one")
        : t("declined_event_many", { count }),
    [t],
  );

  const getRelativeTime = useCallback(
    ({ startTime }: { startTime: number }) => {
      const { eventState, hours, minutes } = calculateEventTimeInfo(startTime);

      // Format the time string using translations
      switch (eventState) {
        case "in_progress":
          if (hours === 0 && minutes === 0) {
            return t("now");
          }
          return hours > 0
            ? hours === 1
              ? t("hour_ago", { hours })
              : t("hours_ago", { hours })
            : t("minutes_ago", { minutes });
        case "upcoming": {
          let adjustedMinutes = minutes;
          if (hours === 0 && minutes === 0) {
            adjustedMinutes = 1;
          }
          return hours > 0
            ? hours === 1
              ? t("hour_until", { hours })
              : t("hours_until", { hours })
            : t("minutes_until", { minutes: adjustedMinutes });
        }
        default:
          return "";
      }
    },
    [t],
  );

  return {
    getFormattedEventTime,
    getNumAttendeesLabel,
    getDeclinedEventLabel,
    getRelativeTime,
    dateFormatterFn,
    getFullDate,
    attendingLabel: t("event_attending_label"),
    descriptionLabel: t("event_description_label"),
    timeLabel: t("time_label"),
    showMore: t("show_more_description"),
    showLess: t("show_less_description"),
    guestLabel: t("guest_label"),
    hostLabel: t("host_label"),
    relativeTimeLabel: t("relative_time_label"),
  };
};
